import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { payrollApi, Payslip } from '../services/payroll';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import { useCompany } from '../contexts/CompanyContext';

const PayslipDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payslip, setPayslip] = useState<Payslip | null>(null);
  const [employeeName, setEmployeeName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { company } = useCompany();

  useEffect(() => {
    if (id) {
      loadPayslip();
    }
  }, [id]);

  const loadPayslip = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      // Get all payslips and find the one we need
      const payslipData = await payrollApi.getPayslips();
      const foundPayslip = payslipData.find(p => p.id === id);
      
      if (!foundPayslip) {
        setError('Payslip not found');
        setLoading(false);
        return;
      }
      
      setPayslip(foundPayslip);
      
      // Get employee name
      const employees = await payrollApi.getEmployees();
      const employee = employees.find(e => e.id === foundPayslip.employeeId);
      setEmployeeName(employee?.name || 'Unknown');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payslip');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!payslip) return;
    
    try {
      setError(null);
      const blob = await payrollApi.downloadPayslipPDF(payslip.id);
      
      if (blob.type && blob.type !== 'application/pdf') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          setError(errorData.message || 'Failed to download PDF');
          return;
        } catch {
          setError('Failed to download PDF: Invalid response format');
          return;
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-${payslip.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('PDF download error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to download PDF';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payslip) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payroll')}
          sx={{ mb: 2 }}
        >
          Back to Payroll
        </Button>
        <Alert severity="error">{error || 'Payslip not found'}</Alert>
      </Box>
    );
  }

  const totalDeductions = payslip.incomeTax + payslip.socialSecurity;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payroll')}
        >
          Back to Payroll
        </Button>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPDF}
        >
          Download PDF
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Payslip Document */}
      <Paper sx={{ p: 4 }}>
        {/* Company Header */}
        {company && (
          <Box mb={4}>
            {company.logoUrl && (
              <Box mb={2}>
                <img
                  src={company.logoUrl}
                  alt={company.name}
                  style={{ maxHeight: '80px', maxWidth: '200px' }}
                />
              </Box>
            )}
            <Typography variant="h5" fontWeight="bold">
              {company.name}
            </Typography>
            {company.location && (
              <Typography variant="body2" color="text.secondary">
                {company.location}
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          PAYSLIP
        </Typography>

        <Grid container spacing={4} mb={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Pay Period: {format(parseISO(payslip.payPeriodStart), 'MMM d, yyyy')} - {format(parseISO(payslip.payPeriodEnd), 'MMM d, yyyy')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Payment Date: {format(parseISO(payslip.payPeriodEnd), 'MMM d, yyyy')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Employee Details */}
        <Box mb={4}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Employee Details:
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Name:</strong> {employeeName}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Earnings */}
        <Box mb={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            EARNINGS
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Gross Salary</TableCell>
                  <TableCell align="right">
                    {formatCurrency(payslip.grossSalary, payslip.currency)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* Deductions */}
        <Box mb={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            DEDUCTIONS
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Income Tax</TableCell>
                  <TableCell align="right">
                    {formatCurrency(payslip.incomeTax, payslip.currency)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Social Security</TableCell>
                  <TableCell align="right">
                    {formatCurrency(payslip.socialSecurity, payslip.currency)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      Total Deductions
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(totalDeductions, payslip.currency)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* Net Pay */}
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            NET PAY
          </Typography>
          <Divider sx={{ mb: 2, borderWidth: 2 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            {formatCurrency(payslip.netSalary, payslip.currency)}
          </Typography>
          <Divider sx={{ mt: 2, borderWidth: 2 }} />
        </Box>
      </Paper>
    </Box>
  );
};

export default PayslipDetail;
